/**
 * School API
 *
 * Mock school administration service.
 */

import type {
  School,
  SchoolClass,
  StudentRoster,
  TeacherRoster,
  ClassAssignment,
  StudentAssignmentProgress,
  SchoolDashboardStats,
  ClassDashboardStats,
  CSVImportResult,
  PurchaseRequest,
  Challenge,
  GradeLevel,
  ChallengeRequest,
  Survey,
  SurveyCampaign,
  GraduatingStudent,
  UserChallenge,
  ApiResponse,
  UUID,
  User,
  TeacherInvitation,
  SchoolAnalyticsData,
  EngagementDataPoint,
  ClassAnalyticsRow,
  SurveyTemplate,
  SurveyReminder,
} from '../types'
import { mockDb, delay, generateId, timestamp } from './mockDb'
import { success, error } from './client'
import { notificationApi } from './notification.api'
import type { Notification } from '../types'

/** Fire a school_admin notification to all admins of the given school. */
async function notifySchoolAdmins(
  schoolId: string,
  title: string,
  message: string,
  actionUrl?: string,
  relatedType?: Notification['relatedType'],
  relatedId?: string,
) {
  const admins = mockDb.findWhere<TeacherRoster>(
    'teacherRoster',
    (t) => t.schoolId === schoolId && t.role === 'admin' && t.status === 'active'
  )
  for (const admin of admins) {
    await notificationApi.createNotification({
      userId: admin.userId,
      category: 'school_admin',
      priority: 'medium',
      title,
      message,
      actionUrl,
      relatedType,
      relatedId,
    })
  }
}

/** Fire a notification to a specific user. */
async function notifyUser(
  userId: string,
  title: string,
  message: string,
  actionUrl?: string,
  relatedType?: Notification['relatedType'],
  relatedId?: string,
) {
  await notificationApi.createNotification({
    userId,
    category: 'school_admin',
    priority: 'medium',
    title,
    message,
    actionUrl,
    relatedType,
    relatedId,
  })
}

// Session key (shared)
const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

/**
 * School API
 */
export const schoolApi = {
  // ============================================================================
  // Schools
  // ============================================================================

  /**
   * Get current user's school (if school admin/teacher)
   */
  async getMySchool(): Promise<ApiResponse<School | null>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Find school where user is admin or teacher
    const teacherEntry = mockDb.findOne<TeacherRoster>(
      'teacherRoster',
      (t) => t.userId === userId && t.status === 'active'
    )

    if (!teacherEntry) {
      return success(null)
    }

    const school = mockDb.findById<School>('schools', teacherEntry.schoolId)
    return success(school)
  },

  /**
   * Get school by ID
   */
  async getSchoolById(schoolId: UUID): Promise<ApiResponse<School | null>> {
    await delay(50)

    const school = mockDb.findById<School>('schools', schoolId)
    return success(school)
  },

  /**
   * Update school settings
   */
  async updateSchool(
    schoolId: UUID,
    data: Partial<Omit<School, 'id' | 'createdAt' | 'updatedAt' | 'studentCount' | 'teacherCount' | 'classCount'>>
  ): Promise<ApiResponse<School>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Verify user is school admin
    const teacherEntry = mockDb.findOne<TeacherRoster>(
      'teacherRoster',
      (t) => t.userId === userId && t.schoolId === schoolId && t.role === 'admin'
    )

    if (!teacherEntry) {
      return error('Not authorized')
    }

    const updated = mockDb.update<School>('schools', schoolId, data)
    if (!updated) {
      return error('School not found')
    }

    return success(updated)
  },

  /**
   * Get school dashboard stats
   */
  async getSchoolStats(schoolId: UUID): Promise<ApiResponse<SchoolDashboardStats>> {
    await delay(100)

    const students = mockDb.findWhere<StudentRoster>(
      'studentRoster',
      (s) => s.schoolId === schoolId && s.status === 'active'
    )

    const teachers = mockDb.findWhere<TeacherRoster>(
      'teacherRoster',
      (t) => t.schoolId === schoolId && t.status === 'active'
    )

    const classes = mockDb.findWhere<SchoolClass>(
      'classes',
      (c) => c.schoolId === schoolId && c.isActive
    )

    const assignments = mockDb.findWhere<ClassAssignment>(
      'classAssignments',
      (a) => {
        const cls = mockDb.findById<SchoolClass>('classes', a.classId)
        return cls?.schoolId === schoolId && a.status === 'active'
      }
    )

    const stats: SchoolDashboardStats = {
      totalStudents: students.length,
      totalTeachers: teachers.length,
      totalClasses: classes.length,
      activeAssignments: assignments.length,
      completedChallenges: 0, // Would calculate from track records
      averageCompletionRate: 0,
    }

    return success(stats)
  },

  // ============================================================================
  // Classes
  // ============================================================================

  /**
   * Get all classes for a school
   */
  async getClasses(schoolId: UUID): Promise<ApiResponse<SchoolClass[]>> {
    await delay(100)

    const classes = mockDb
      .findWhere<SchoolClass>('classes', (c) => c.schoolId === schoolId)
      .sort((a, b) => a.name.localeCompare(b.name))

    // Populate teacher
    const populatedClasses = classes.map((cls) => {
      const teacher = mockDb.findById<User>('users', cls.primaryTeacherId)
      return {
        ...cls,
        primaryTeacher: teacher
          ? { id: teacher.id, displayName: teacher.displayName }
          : undefined,
      }
    })

    return success(populatedClasses)
  },

  /**
   * Create a class
   */
  async createClass(
    schoolId: UUID,
    data: { name: string; description?: string; gradeLevel?: string; primaryTeacherId: UUID }
  ): Promise<ApiResponse<SchoolClass>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const cls = mockDb.insert<SchoolClass>('classes', {
      schoolId,
      name: data.name,
      description: data.description,
      gradeLevel: data.gradeLevel,
      primaryTeacherId: data.primaryTeacherId,
      isActive: true,
      studentCount: 0,
      assignmentCount: 0,
    })

    // Update school class count
    const school = mockDb.findById<School>('schools', schoolId)
    if (school) {
      mockDb.update<School>('schools', schoolId, {
        classCount: school.classCount + 1,
      })
    }

    return success(cls)
  },

  /**
   * Update a class
   */
  async updateClass(
    classId: UUID,
    data: Partial<Pick<SchoolClass, 'name' | 'description' | 'gradeLevel' | 'primaryTeacherId' | 'isActive'>>
  ): Promise<ApiResponse<SchoolClass>> {
    await delay(100)

    const updated = mockDb.update<SchoolClass>('classes', classId, data)
    if (!updated) {
      return error('Class not found')
    }

    return success(updated)
  },

  /**
   * Get class dashboard stats
   */
  async getClassStats(classId: UUID): Promise<ApiResponse<ClassDashboardStats>> {
    await delay(100)

    const students = mockDb.findWhere<StudentRoster>(
      'studentRoster',
      (s) => s.classId === classId && s.status === 'active'
    )

    const assignments = mockDb.findWhere<ClassAssignment>(
      'classAssignments',
      (a) => a.classId === classId && a.status === 'active'
    )

    const stats: ClassDashboardStats = {
      studentCount: students.length,
      activeAssignments: assignments.length,
      averageProgress: 0,
      topPerformers: [],
    }

    return success(stats)
  },

  // ============================================================================
  // Students
  // ============================================================================

  /**
   * Get students for a school or class
   */
  async getStudents(schoolId: UUID, classId?: UUID): Promise<ApiResponse<StudentRoster[]>> {
    await delay(100)

    let students = mockDb.findWhere<StudentRoster>(
      'studentRoster',
      (s) => s.schoolId === schoolId
    )

    if (classId) {
      students = students.filter((s) => s.classId === classId)
    }

    // Populate user info
    const populatedStudents = students.map((student) => {
      const user = mockDb.findById<User>('users', student.userId)
      return {
        ...student,
        user: user
          ? { id: user.id, displayName: user.displayName, email: user.email }
          : undefined,
      }
    })

    return success(populatedStudents)
  },

  /**
   * Add student to roster
   */
  async addStudent(
    schoolId: UUID,
    data: { userId: UUID; classId?: UUID; studentId?: string; gradeLevel?: string }
  ): Promise<ApiResponse<StudentRoster>> {
    await delay(100)

    // Check if already enrolled
    const existing = mockDb.findOne<StudentRoster>(
      'studentRoster',
      (s) => s.schoolId === schoolId && s.userId === data.userId
    )

    if (existing) {
      return error('Student already enrolled')
    }

    const student = mockDb.insert<StudentRoster>('studentRoster', {
      schoolId,
      classId: data.classId,
      userId: data.userId,
      studentId: data.studentId,
      gradeLevel: data.gradeLevel,
      status: 'active',
      enrolledAt: new Date().toISOString(),
    })

    // Update counts
    const school = mockDb.findById<School>('schools', schoolId)
    if (school) {
      mockDb.update<School>('schools', schoolId, {
        studentCount: school.studentCount + 1,
      })
    }

    if (data.classId) {
      const cls = mockDb.findById<SchoolClass>('classes', data.classId)
      if (cls) {
        mockDb.update<SchoolClass>('classes', data.classId, {
          studentCount: cls.studentCount + 1,
        })
      }
    }

    // Notify school admins
    notifySchoolAdmins(schoolId, 'New Student Enrolled', 'A new student was added to the roster', '/school/roster')

    return success(student)
  },

  /**
   * Remove student from roster
   */
  async removeStudent(studentRosterId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const student = mockDb.findById<StudentRoster>('studentRoster', studentRosterId)
    if (!student) {
      return error('Student not found')
    }

    mockDb.update<StudentRoster>('studentRoster', studentRosterId, {
      status: 'inactive',
    })

    // Update counts
    const school = mockDb.findById<School>('schools', student.schoolId)
    if (school) {
      mockDb.update<School>('schools', student.schoolId, {
        studentCount: Math.max(0, school.studentCount - 1),
      })
    }

    if (student.classId) {
      const cls = mockDb.findById<SchoolClass>('classes', student.classId)
      if (cls) {
        mockDb.update<SchoolClass>('classes', student.classId, {
          studentCount: Math.max(0, cls.studentCount - 1),
        })
      }
    }

    // Notify school admins
    notifySchoolAdmins(student.schoolId, 'Student Removed', 'A student was removed from the roster', '/school/roster')

    return success(undefined)
  },

  /**
   * Import students from CSV (mock)
   */
  async importStudentsCSV(_schoolId: UUID, _csvData: string): Promise<ApiResponse<CSVImportResult>> {
    await delay(500) // Simulate processing

    // Mock import result
    const result: CSVImportResult = {
      success: true,
      totalRows: 10,
      importedCount: 8,
      skippedCount: 2,
      errors: [
        { row: 3, message: 'Invalid email format' },
        { row: 7, message: 'Duplicate student ID' },
      ],
    }

    return success(result)
  },

  // ============================================================================
  // Teachers
  // ============================================================================

  /**
   * Get teachers for a school
   */
  async getTeachers(schoolId: UUID): Promise<ApiResponse<TeacherRoster[]>> {
    await delay(100)

    const teachers = mockDb.findWhere<TeacherRoster>(
      'teacherRoster',
      (t) => t.schoolId === schoolId
    )

    // Populate user info
    const populatedTeachers = teachers.map((teacher) => {
      const user = mockDb.findById<User>('users', teacher.userId)
      return {
        ...teacher,
        user: user
          ? { id: user.id, displayName: user.displayName, email: user.email }
          : undefined,
      }
    })

    return success(populatedTeachers)
  },

  /**
   * Add teacher to roster
   */
  async addTeacher(
    schoolId: UUID,
    data: { userId: UUID; role: 'teacher' | 'admin' | 'staff'; department?: string }
  ): Promise<ApiResponse<TeacherRoster>> {
    await delay(100)

    // Check if already on roster
    const existing = mockDb.findOne<TeacherRoster>(
      'teacherRoster',
      (t) => t.schoolId === schoolId && t.userId === data.userId
    )

    if (existing) {
      return error('Teacher already on roster')
    }

    const teacher = mockDb.insert<TeacherRoster>('teacherRoster', {
      schoolId,
      userId: data.userId,
      role: data.role,
      department: data.department,
      status: 'active',
    })

    // Update count
    const school = mockDb.findById<School>('schools', schoolId)
    if (school) {
      mockDb.update<School>('schools', schoolId, {
        teacherCount: school.teacherCount + 1,
      })
    }

    return success(teacher)
  },

  // ============================================================================
  // Assignments
  // ============================================================================

  /**
   * Get assignments for a class
   */
  async getClassAssignments(classId: UUID): Promise<ApiResponse<ClassAssignment[]>> {
    await delay(100)

    const assignments = mockDb
      .findWhere<ClassAssignment>('classAssignments', (a) => a.classId === classId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    // Populate challenge
    const populatedAssignments = assignments.map((assignment) => ({
      ...assignment,
      challenge: mockDb.findById<Challenge>('challenges', assignment.challengeId) ?? undefined,
    }))

    return success(populatedAssignments)
  },

  /**
   * Create an assignment
   */
  async createAssignment(
    classId: UUID,
    data: { challengeId: UUID; title?: string; instructions?: string; dueDate?: string }
  ): Promise<ApiResponse<ClassAssignment>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const cls = mockDb.findById<SchoolClass>('classes', classId)
    if (!cls) {
      return error('Class not found')
    }

    const assignment = mockDb.insert<ClassAssignment>('classAssignments', {
      classId,
      challengeId: data.challengeId,
      assignedByUserId: userId,
      title: data.title,
      instructions: data.instructions,
      dueDate: data.dueDate,
      status: 'active',
      totalStudents: cls.studentCount,
      completedCount: 0,
      submittedCount: 0,
    })

    // Update class assignment count
    mockDb.update<SchoolClass>('classes', classId, {
      assignmentCount: cls.assignmentCount + 1,
    })

    assignment.challenge = mockDb.findById<Challenge>('challenges', data.challengeId) ?? undefined

    // Notify school admins
    notifySchoolAdmins(
      cls.schoolId,
      'New Assignment Created',
      `${assignment.title ?? 'A new assignment'} was created for ${cls.name}`,
      `/school/classes/${classId}/progress`,
      'assignment',
      assignment.id,
    )

    return success(assignment)
  },

  /**
   * Update an assignment
   */
  async updateAssignment(
    assignmentId: UUID,
    data: Partial<Pick<ClassAssignment, 'title' | 'instructions' | 'dueDate' | 'status'>>
  ): Promise<ApiResponse<ClassAssignment>> {
    await delay(100)

    const updated = mockDb.update<ClassAssignment>('classAssignments', assignmentId, data)
    if (!updated) {
      return error('Assignment not found')
    }

    return success(updated)
  },

  /**
   * Get student progress for an assignment
   */
  async getAssignmentProgress(assignmentId: UUID): Promise<ApiResponse<StudentAssignmentProgress[]>> {
    await delay(100)

    const progress = mockDb.findWhere<StudentAssignmentProgress>(
      'assignmentProgress',
      (p) => p.assignmentId === assignmentId
    )

    return success(progress)
  },

  // ============================================================================
  // Grade Levels
  // ============================================================================

  /**
   * Get grade levels for a school
   */
  async getGradeLevels(schoolId: UUID): Promise<ApiResponse<GradeLevel[]>> {
    await delay(50)

    const gradeLevels = mockDb
      .findWhere<GradeLevel>('gradeLevels', (g) => g.schoolId === schoolId)
      .sort((a, b) => a.displayOrder - b.displayOrder)

    return success(gradeLevels)
  },

  /**
   * Create a grade level
   */
  async createGradeLevel(
    schoolId: UUID,
    data: { name: string; displayOrder: number }
  ): Promise<ApiResponse<GradeLevel>> {
    await delay(100)

    const gradeLevel = mockDb.insert<GradeLevel>('gradeLevels', {
      schoolId,
      name: data.name,
      displayOrder: data.displayOrder,
      isActive: true,
      studentCount: 0,
    })

    return success(gradeLevel)
  },

  /**
   * Update a grade level
   */
  async updateGradeLevel(
    gradeId: UUID,
    data: { name?: string; isActive?: boolean; displayOrder?: number }
  ): Promise<ApiResponse<GradeLevel>> {
    await delay(100)

    const updated = mockDb.update<GradeLevel>('gradeLevels', gradeId, data)
    if (!updated) {
      return error('Grade level not found')
    }

    return success(updated)
  },

  /**
   * Delete a grade level
   */
  async deleteGradeLevel(gradeId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const gradeLevel = mockDb.findById<GradeLevel>('gradeLevels', gradeId)
    if (!gradeLevel) {
      return error('Grade level not found')
    }

    if (gradeLevel.studentCount > 0) {
      return error('Cannot delete grade level with students assigned')
    }

    mockDb.delete('gradeLevels', gradeId)
    return success(undefined)
  },

  // ============================================================================
  // Purchase Requests
  // ============================================================================

  /**
   * Get purchase requests for a school
   */
  async getPurchaseRequests(
    schoolId: UUID,
    status?: 'pending' | 'approved' | 'denied'
  ): Promise<ApiResponse<PurchaseRequest[]>> {
    await delay(100)

    let requests = mockDb.findWhere<PurchaseRequest>(
      'purchaseRequests',
      (r) => r.schoolId === schoolId
    )

    if (status) {
      requests = requests.filter((r) => r.status === status)
    }

    // Populate
    const populated = requests
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .map((req) => ({
        ...req,
        requestedBy: (() => {
          const user = mockDb.findById<User>('users', req.requestedByUserId)
          return user ? { id: user.id, displayName: user.displayName } : undefined
        })(),
        challenge: mockDb.findById<Challenge>('challenges', req.challengeId) ?? undefined,
        class: req.classId
          ? (() => {
              const cls = mockDb.findById<SchoolClass>('classes', req.classId)
              return cls ? { id: cls.id, name: cls.name } : undefined
            })()
          : undefined,
      }))

    return success(populated)
  },

  /**
   * Create a purchase request (teacher action)
   */
  async createPurchaseRequest(
    schoolId: UUID,
    data: {
      challengeId: UUID
      classId?: UUID
      studentCount: number
      reason?: string
      estimatedCost: number
    }
  ): Promise<ApiResponse<PurchaseRequest>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const request = mockDb.insert<PurchaseRequest>('purchaseRequests', {
      schoolId,
      requestedByUserId: userId,
      challengeId: data.challengeId,
      classId: data.classId,
      studentCount: data.studentCount,
      reason: data.reason,
      estimatedCost: data.estimatedCost,
      status: 'pending',
    })

    return success(request)
  },

  /**
   * Review a purchase request (SA action)
   */
  async reviewPurchaseRequest(
    requestId: UUID,
    decision: 'approved' | 'denied',
    note?: string
  ): Promise<ApiResponse<PurchaseRequest>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const updated = mockDb.update<PurchaseRequest>('purchaseRequests', requestId, {
      status: decision,
      reviewedByUserId: userId,
      reviewedAt: new Date().toISOString(),
      reviewNote: note,
    })

    if (!updated) {
      return error('Request not found')
    }

    // Populate
    const user = mockDb.findById<User>('users', updated.requestedByUserId)
    updated.requestedBy = user ? { id: user.id, displayName: user.displayName } : undefined
    updated.challenge = mockDb.findById<Challenge>('challenges', updated.challengeId) ?? undefined

    return success(updated)
  },

  // ============================================================================
  // Student Suspension
  // ============================================================================

  /**
   * Suspend a student from class
   */
  async suspendStudent(
    studentRosterId: UUID,
    reason?: string
  ): Promise<ApiResponse<StudentRoster>> {
    await delay(100)

    const student = mockDb.findById<StudentRoster>('studentRoster', studentRosterId)
    if (!student) {
      return error('Student not found')
    }

    if (student.status !== 'active') {
      return error('Only active students can be suspended')
    }

    const updated = mockDb.update<StudentRoster>('studentRoster', studentRosterId, {
      status: 'suspended',
      suspendedAt: new Date().toISOString(),
      suspendedReason: reason,
    })

    return success(updated!)
  },

  /**
   * Reinstate a suspended student
   */
  async reinstateStudent(studentRosterId: UUID): Promise<ApiResponse<StudentRoster>> {
    await delay(100)

    const student = mockDb.findById<StudentRoster>('studentRoster', studentRosterId)
    if (!student) {
      return error('Student not found')
    }

    if (student.status !== 'suspended') {
      return error('Only suspended students can be reinstated')
    }

    const updated = mockDb.update<StudentRoster>('studentRoster', studentRosterId, {
      status: 'active',
      reinstatedAt: new Date().toISOString(),
    })

    return success(updated!)
  },

  // ============================================================================
  // Challenge Requests (Teacher -> SA)
  // ============================================================================

  /**
   * Create a challenge purchase request (teacher -> SA)
   */
  async createChallengeRequest(
    schoolId: UUID,
    data: { challengeId: UUID; reason?: string; numberOfStudents?: number; classId?: UUID }
  ): Promise<ApiResponse<ChallengeRequest>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Check for duplicate pending request
    const existing = mockDb.findWhere<ChallengeRequest>(
      'challengeRequests',
      (r) =>
        r.schoolId === schoolId &&
        r.challengeId === data.challengeId &&
        r.requestedByUserId === userId &&
        r.status === 'pending'
    )
    if (existing.length > 0) {
      return error('You already have a pending request for this challenge')
    }

    const request = mockDb.insert<ChallengeRequest>('challengeRequests', {
      schoolId,
      challengeId: data.challengeId,
      requestedByUserId: userId,
      reason: data.reason,
      numberOfStudents: data.numberOfStudents,
      classId: data.classId,
      status: 'pending',
    })

    // Notify school admins about the purchase request
    notifySchoolAdmins(
      schoolId,
      'Purchase Request Submitted',
      'A teacher requested purchase of a challenge',
      '/school/purchase-requests',
      'challenge',
      data.challengeId,
    )

    return success(request)
  },

  /**
   * Get challenge requests for a school (SA view)
   */
  async getChallengeRequests(
    schoolId: UUID,
    status?: 'pending' | 'approved' | 'denied'
  ): Promise<ApiResponse<ChallengeRequest[]>> {
    await delay(100)

    let requests = mockDb.findWhere<ChallengeRequest>(
      'challengeRequests',
      (r) => r.schoolId === schoolId
    )

    if (status) {
      requests = requests.filter((r) => r.status === status)
    }

    // Populate challenge and user info
    const populated = requests.map((r) => {
      const challenge = mockDb.findById<Challenge>('challenges', r.challengeId)
      const user = mockDb.findById<User>('users', r.requestedByUserId)
      return {
        ...r,
        challenge: challenge ?? undefined,
        requestedBy: user ? { id: user.id, displayName: user.displayName } : undefined,
      }
    })

    populated.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    return success(populated)
  },

  /**
   * Review a challenge request (SA approves/denies)
   */
  async reviewChallengeRequest(
    requestId: UUID,
    decision: { status: 'approved' | 'denied'; reviewNote?: string }
  ): Promise<ApiResponse<ChallengeRequest>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const original = mockDb.findById<ChallengeRequest>('challengeRequests', requestId)

    const updated = mockDb.update<ChallengeRequest>('challengeRequests', requestId, {
      status: decision.status,
      reviewedByUserId: userId,
      reviewedAt: new Date().toISOString(),
      reviewNote: decision.reviewNote,
    })

    if (!updated) {
      return error('Request not found')
    }

    // Notify the requesting teacher about the decision
    if (original) {
      notifyUser(
        original.requestedByUserId,
        `Challenge Request ${decision.status === 'approved' ? 'Approved' : 'Denied'}`,
        decision.reviewNote ?? `Your challenge request was ${decision.status}.`,
        '/school/purchase-requests',
        'challenge',
        original.challengeId,
      )
    }

    return success(updated)
  },

  // ============================================================================
  // Surveys (SA)
  // ============================================================================

  /**
   * Create a survey
   */
  async createSurvey(
    schoolId: UUID,
    data: { title: string; description?: string; externalUrl: string }
  ): Promise<ApiResponse<Survey>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const survey = mockDb.insert<Survey>('surveys', {
      schoolId,
      createdByUserId: userId,
      title: data.title,
      description: data.description,
      externalUrl: data.externalUrl,
      status: 'draft',
    })

    return success(survey)
  },

  /**
   * Get all surveys for a school
   */
  async getSurveys(schoolId: UUID): Promise<ApiResponse<Survey[]>> {
    await delay(100)

    const surveys = mockDb.findWhere<Survey>('surveys', (s) => s.schoolId === schoolId)
    surveys.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    return success(surveys)
  },

  /**
   * Update a survey
   */
  async updateSurvey(
    surveyId: UUID,
    data: Partial<Pick<Survey, 'title' | 'description' | 'externalUrl' | 'status'>>
  ): Promise<ApiResponse<Survey>> {
    await delay(100)

    const updated = mockDb.update<Survey>('surveys', surveyId, data)
    if (!updated) {
      return error('Survey not found')
    }

    return success(updated)
  },

  /**
   * Delete a draft survey
   */
  async deleteSurvey(surveyId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const survey = mockDb.findById<Survey>('surveys', surveyId)
    if (!survey) {
      return error('Survey not found')
    }

    if (survey.status !== 'draft') {
      return error('Only draft surveys can be deleted')
    }

    mockDb.delete('surveys', surveyId)
    return success(undefined)
  },

  /**
   * Create a survey campaign (distribute to audience)
   */
  async createCampaign(
    surveyId: UUID,
    data: { targetAudience: SurveyCampaign['targetAudience']; targetClassId?: UUID; targetGradeLevel?: string }
  ): Promise<ApiResponse<SurveyCampaign>> {
    await delay(100)

    const survey = mockDb.findById<Survey>('surveys', surveyId)
    if (!survey) {
      return error('Survey not found')
    }

    // Simulate distributed count based on audience
    const distributedCount =
      data.targetAudience === 'all_students' ? 120 :
      data.targetAudience === 'all_teachers' ? 15 :
      data.targetAudience === 'specific_class' ? 25 : 30

    const campaign = mockDb.insert<SurveyCampaign>('surveyCampaigns', {
      surveyId,
      schoolId: survey.schoolId,
      targetAudience: data.targetAudience,
      targetClassId: data.targetClassId,
      targetGradeLevel: data.targetGradeLevel,
      distributedCount,
      clickedCount: 0,
      status: 'sent',
      sentAt: new Date().toISOString(),
    })

    // Mark survey as active
    mockDb.update<Survey>('surveys', surveyId, { status: 'active' })

    return success(campaign)
  },

  /**
   * Get campaigns for a survey
   */
  async getCampaigns(surveyId: UUID): Promise<ApiResponse<SurveyCampaign[]>> {
    await delay(100)

    const campaigns = mockDb.findWhere<SurveyCampaign>(
      'surveyCampaigns',
      (c) => c.surveyId === surveyId
    )

    return success(campaigns)
  },

  // ============================================================================
  // Alumni Transition
  // ============================================================================

  /**
   * Get graduating students for the alumni transition workflow.
   * Queries studentRoster for students in the graduating grade level,
   * joins with users for contact info, and counts completed challenges.
   */
  async getGraduatingStudents(schoolId: UUID): Promise<ApiResponse<GraduatingStudent[]>> {
    await delay(150)

    // Find students in the graduating grade (8th grade by default)
    const rosterEntries = mockDb.findWhere<StudentRoster>(
      'studentRoster',
      (s) => s.schoolId === schoolId && s.gradeLevel === '8th' && s.status === 'active'
    )

    const graduatingStudents: GraduatingStudent[] = rosterEntries.map((entry) => {
      const user = mockDb.findById<User>('users', entry.userId)

      // Count completed challenges for this student
      const completedChallenges = mockDb.findWhere<UserChallenge>(
        'userChallenges',
        (uc) => uc.userId === entry.userId && uc.status === 'completed'
      )

      return {
        id: entry.id,
        name: user?.displayName ?? 'Unknown Student',
        grade: entry.gradeLevel ?? '8th',
        graduationDate: new Date(new Date().getFullYear(), 5, 15).toISOString(),
        email: user?.email ?? '',
        challengesCompleted: completedChallenges.length,
        status: 'pending' as const,
      }
    })

    return success(graduatingStudents)
  },

  /**
   * Transition a single student to a personal account.
   * Updates their roster status to reflect the transition.
   */
  async transitionStudent(studentId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const student = mockDb.findById<StudentRoster>('studentRoster', studentId)
    if (!student) {
      return error('Student not found')
    }

    mockDb.update<StudentRoster>('studentRoster', studentId, {
      status: 'transferred',
    })

    return success(undefined)
  },

  /**
   * Archive a graduating student's account.
   * The account becomes read-only and the student can no longer log in.
   */
  async archiveStudent(studentId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const student = mockDb.findById<StudentRoster>('studentRoster', studentId)
    if (!student) {
      return error('Student not found')
    }

    mockDb.update<StudentRoster>('studentRoster', studentId, {
      status: 'inactive',
    })

    return success(undefined)
  },

  /**
   * Export all student data and then permanently delete the student record.
   */
  async exportAndDeleteStudent(studentId: UUID): Promise<ApiResponse<void>> {
    await delay(200)

    const student = mockDb.findById<StudentRoster>('studentRoster', studentId)
    if (!student) {
      return error('Student not found')
    }

    // Remove from roster (simulates export + delete)
    mockDb.delete('studentRoster', studentId)

    // Update school counts
    const school = mockDb.findById<School>('schools', student.schoolId)
    if (school) {
      mockDb.update<School>('schools', student.schoolId, {
        studentCount: Math.max(0, school.studentCount - 1),
      })
    }

    return success(undefined)
  },

  /**
   * Batch transition all specified students to personal accounts.
   */
  async batchTransitionStudents(studentIds: UUID[]): Promise<ApiResponse<void>> {
    await delay(200)

    for (const studentId of studentIds) {
      const student = mockDb.findById<StudentRoster>('studentRoster', studentId)
      if (student) {
        mockDb.update<StudentRoster>('studentRoster', studentId, {
          status: 'transferred',
        })
      }
    }

    return success(undefined)
  },

  // ============================================================================
  // Roster CSV Export
  // ============================================================================

  async exportStudentsCSV(schoolId: UUID): Promise<ApiResponse<string>> {
    await delay(150)

    const students = mockDb.findWhere<StudentRoster>(
      'studentRoster',
      (s) => s.schoolId === schoolId
    )

    const header = 'Name,Email,Student ID,Grade Level,Status,Enrolled At,Last Login'
    const rows = students.map((s) => {
      const user = mockDb.findById<User>('users', s.userId)
      return [
        user?.displayName ?? '',
        user?.email ?? '',
        s.studentId ?? '',
        s.gradeLevel ?? '',
        s.status,
        s.enrolledAt ?? '',
        s.lastLoginAt ?? '',
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')
    })

    return success([header, ...rows].join('\n'))
  },

  // ============================================================================
  // Teacher Invitations
  // ============================================================================

  async inviteTeacher(
    schoolId: UUID,
    data: { email: string; role: 'teacher' | 'admin' | 'staff'; department?: string }
  ): Promise<ApiResponse<TeacherInvitation>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    // Check for existing pending invite
    const existing = mockDb.findOne<TeacherInvitation>(
      'teacherInvitations',
      (i) => i.schoolId === schoolId && i.email === data.email && i.status === 'pending'
    )
    if (existing) return error('An invitation is already pending for this email')

    const token = generateId()
    const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()

    const invitation = mockDb.insert<TeacherInvitation>('teacherInvitations', {
      schoolId,
      email: data.email,
      role: data.role,
      department: data.department,
      invitedByUserId: userId,
      status: 'pending',
      expiresAt,
      token,
    })

    // Notify school admins
    notifySchoolAdmins(schoolId, 'Teacher Invited', `An invitation was sent to ${data.email}`, '/school/teachers')

    return success(invitation)
  },

  async getPendingInvitations(schoolId: UUID): Promise<ApiResponse<TeacherInvitation[]>> {
    await delay(50)

    const invitations = mockDb.findWhere<TeacherInvitation>(
      'teacherInvitations',
      (i) => i.schoolId === schoolId && i.status === 'pending'
    )

    return success(invitations)
  },

  async cancelInvitation(invitationId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const updated = mockDb.update<TeacherInvitation>('teacherInvitations', invitationId, {
      status: 'cancelled',
    })
    if (!updated) return error('Invitation not found')

    return success(undefined)
  },

  async generateInviteLink(schoolId: UUID): Promise<ApiResponse<{ url: string; token: string; expiresAt: string }>> {
    await delay(50)

    const token = generateId()
    const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()

    return success({
      url: `${window.location.origin}/invite/${token}`,
      token,
      expiresAt,
    })
  },

  async removeTeacher(teacherRosterId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const teacher = mockDb.findById<TeacherRoster>('teacherRoster', teacherRosterId)
    if (!teacher) return error('Teacher not found')

    mockDb.update<TeacherRoster>('teacherRoster', teacherRosterId, {
      status: 'inactive',
    })

    const school = mockDb.findById<School>('schools', teacher.schoolId)
    if (school) {
      mockDb.update<School>('schools', teacher.schoolId, {
        teacherCount: Math.max(0, school.teacherCount - 1),
      })
    }

    return success(undefined)
  },

  // ============================================================================
  // Analytics
  // ============================================================================

  async getAnalytics(
    schoolId: UUID,
    dateRange?: { start: string; end: string }
  ): Promise<ApiResponse<SchoolAnalyticsData>> {
    await delay(150)

    const end = dateRange?.end ? new Date(dateRange.end) : new Date()
    const start = dateRange?.start ? new Date(dateRange.start) : new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000)
    const days = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)))
    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    const students = mockDb.findWhere<StudentRoster>(
      'studentRoster',
      (s) => s.schoolId === schoolId && s.status === 'active'
    )
    const studentCount = students.length || 25

    // Generate engagement trend from mock data
    const engagementTrend: EngagementDataPoint[] = []
    for (let i = 0; i < days; i++) {
      const d = new Date(start.getTime() + i * 24 * 60 * 60 * 1000)
      const isWeekend = d.getDay() === 0 || d.getDay() === 6
      const base = isWeekend ? Math.floor(studentCount * 0.2) : Math.floor(studentCount * 0.6)
      const variance = Math.floor(Math.random() * Math.floor(studentCount * 0.2))
      engagementTrend.push({
        date: d.toISOString().split('T')[0],
        label: dayLabels[d.getDay()],
        activeStudents: base + variance,
        challengesStarted: Math.floor((base + variance) * 0.4),
        challengesCompleted: Math.floor((base + variance) * 0.25),
      })
    }

    // Class comparison from real class data
    const classes = mockDb.findWhere<SchoolClass>('classes', (c) => c.schoolId === schoolId && c.isActive)
    const classComparison: ClassAnalyticsRow[] = classes.map((cls) => {
      const assignments = mockDb.findWhere<ClassAssignment>(
        'classAssignments',
        (a) => a.classId === cls.id
      )
      const totalStudents = assignments.reduce((s, a) => s + a.totalStudents, 0) || 1
      const completed = assignments.reduce((s, a) => s + a.completedCount, 0)
      return {
        classId: cls.id,
        className: cls.name,
        studentCount: cls.studentCount,
        challengesCompleted: completed,
        averageCompletionRate: Math.round((completed / totalStudents) * 100),
      }
    })

    const totalAssignments = mockDb.findWhere<ClassAssignment>(
      'classAssignments',
      (a) => classes.some((c) => c.id === a.classId)
    )
    const totalStudentsInAssignments = totalAssignments.reduce((s, a) => s + a.totalStudents, 0) || 1
    const totalCompleted = totalAssignments.reduce((s, a) => s + a.completedCount, 0)
    const overallCompletionRate = Math.round((totalCompleted / totalStudentsInAssignments) * 100)

    return success({
      engagementTrend,
      overallCompletionRate,
      classComparison,
      dateRange: { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] },
    })
  },

  // ============================================================================
  // Survey Templates
  // ============================================================================

  async getSurveyTemplates(schoolId: UUID): Promise<ApiResponse<SurveyTemplate[]>> {
    await delay(50)
    const templates = mockDb.findWhere<SurveyTemplate>(
      'surveyTemplates',
      (t) => t.schoolId === schoolId
    )
    return success(templates)
  },

  async createSurveyTemplate(
    schoolId: UUID,
    data: { title: string; description?: string; externalUrl: string }
  ): Promise<ApiResponse<SurveyTemplate>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const template = mockDb.insert<SurveyTemplate>('surveyTemplates', {
      schoolId,
      title: data.title,
      description: data.description,
      externalUrl: data.externalUrl,
      createdByUserId: userId,
    })

    return success(template)
  },

  async deleteSurveyTemplate(templateId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const template = mockDb.findById<SurveyTemplate>('surveyTemplates', templateId)
    if (!template) return error('Template not found')

    mockDb.delete('surveyTemplates', templateId)
    return success(undefined)
  },

  async createSurveyFromTemplate(schoolId: UUID, templateId: UUID): Promise<ApiResponse<Survey>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const template = mockDb.findById<SurveyTemplate>('surveyTemplates', templateId)
    if (!template) return error('Template not found')

    const survey = mockDb.insert<Survey>('surveys', {
      schoolId,
      createdByUserId: userId,
      title: template.title,
      description: template.description,
      externalUrl: template.externalUrl,
      status: 'draft',
      templateId,
    })

    return success(survey)
  },

  // ============================================================================
  // Survey Scheduling & Reminders
  // ============================================================================

  async scheduleCampaign(
    surveyId: UUID,
    data: {
      targetAudience: SurveyCampaign['targetAudience']
      scheduledAt: string
      targetClassId?: UUID
      targetGradeLevel?: string
    }
  ): Promise<ApiResponse<SurveyCampaign>> {
    await delay(100)

    const survey = mockDb.findById<Survey>('surveys', surveyId)
    if (!survey) return error('Survey not found')

    const distributedCount =
      data.targetAudience === 'all_students' ? 120 :
      data.targetAudience === 'all_teachers' ? 15 :
      data.targetAudience === 'specific_class' ? 25 : 30

    const campaign = mockDb.insert<SurveyCampaign>('surveyCampaigns', {
      surveyId,
      schoolId: survey.schoolId,
      targetAudience: data.targetAudience,
      targetClassId: data.targetClassId,
      targetGradeLevel: data.targetGradeLevel,
      distributedCount,
      clickedCount: 0,
      status: 'scheduled',
      scheduledAt: data.scheduledAt,
    })

    mockDb.update<Survey>('surveys', surveyId, { status: 'active' })

    return success(campaign)
  },

  async createReminder(
    campaignId: UUID,
    scheduledAt: string
  ): Promise<ApiResponse<SurveyReminder>> {
    await delay(100)

    const campaign = mockDb.findById<SurveyCampaign>('surveyCampaigns', campaignId)
    if (!campaign) return error('Campaign not found')

    const reminder = mockDb.insert<SurveyReminder>('surveyReminders', {
      campaignId,
      scheduledAt,
      status: 'scheduled',
    })

    return success(reminder)
  },

  async getReminders(campaignId: UUID): Promise<ApiResponse<SurveyReminder[]>> {
    await delay(50)
    const reminders = mockDb.findWhere<SurveyReminder>(
      'surveyReminders',
      (r) => r.campaignId === campaignId
    )
    return success(reminders)
  },
}
